import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  accessToken: string | null = null;

  constructor(
    private oidcSecurityService: OidcSecurityService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.oidcSecurityService.checkAuth().subscribe(({ isAuthenticated }) => {
        this.isAuthenticatedSubject.next(isAuthenticated);
      });
    }
  }

  login(): void {
    sessionStorage.setItem('storageCodeFlowInProgress', 'true'); // Store login state
    this.oidcSecurityService.authorize();
  }

  logout(): void {
    sessionStorage.clear();
    this.oidcSecurityService.getIdToken().subscribe((idToken) => {
      const logoutUrl =
        `https://${environment.cognito.domain}.auth.${environment.cognito.region}.amazoncognito.com/logout` +
        `?client_id=${environment.cognito.clientId}` +
        `&logout_uri=${encodeURIComponent(
          environment.cognito.postLogoutRedirectUri
        )}` +
        (idToken ? `&id_token_hint=${idToken}` : '');
      // Clear local auth state
      this.isAuthenticatedSubject.next(false);
      // Redirect to Cognito Hosted UI logout
      window.location.href = logoutUrl;
    });
  }

  checkAuth(): Observable<boolean> {
    return this.oidcSecurityService.checkAuth().pipe(
      map(({ isAuthenticated, accessToken }) => {
        this.accessToken = accessToken;
        this.isAuthenticatedSubject.next(isAuthenticated);
        return isAuthenticated;
      })
    );
  }

  getUserData(): Observable<any> {
    return this.oidcSecurityService.userData$;
  }

  getAccessToken(): Observable<string> {
    return this.oidcSecurityService.getAccessToken();
  }

  getIdToken(): Observable<string> {
    return this.oidcSecurityService.getIdToken();
  }

  isAccessTokenValid(): Observable<boolean> {
    return this.getAccessToken().pipe(
      map((token) => {
        if (!token) return false;
        const decoded = this.decodeToken(token);
        const exp = decoded?.exp;
        if (!exp) return false;
        return exp * 1000 > Date.now(); // Check expiration
      })
    );
  }

  private decodeToken(token: string): any {
    if (!isPlatformBrowser(this.platformId)) {
      return null; // Avoid decoding in SSR
    }

    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch (e) {
      console.error('Invalid token format', e);
      return null;
    }
  }

  getUserGroups(): Observable<string[]> {
    return this.getAccessToken().pipe(
      map((token) => {
        const decoded = this.decodeToken(token);
        return decoded?.['cognito:groups'] || [];
      })
    );
  }

  hasRole(role: string): Observable<boolean> {
    return this.getUserGroups().pipe(map((groups) => groups.includes(role)));
  }

  getDefaultDashboardUrl(): Observable<string> {
    return this.getUserGroups().pipe(
      map((groups) => {
        if (groups?.includes('admin')) {
          return '/admin/dashboard';
        }
        if (groups?.includes('reseller')) {
          return '/partner/dashboard';
        }
        return '/user/dashboard';
      })
    );
  }
}
